// Free gift in the cart page and drawer. Mirrors the free-gift checkout extension
// (grafikr-sync/extensions/free-gift): same campaigns, same rules.
//
// - Adds the gift line (property `_free_gift` = campaign handle) when the cart meets the campaign
//   minimum and, if set, contains at least one required product; removes it when either fails.
// - A gift the customer removes is remembered in the cart attribute `_free_gift_declined`.
//   It stays removed while both requirements still pass. Once the total drops below the minimum
//   or a required product leaves the cart, the attribute is cleared and the gift can return.
(() => {
  const PROP = "_free_gift";
  const ATTR = "_free_gift_declined";
  const root = () => window.theme?.routes?.cart || "/cart";
  const post = (path, body) =>
    fetch(`${root()}/${path}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${path}: ${r.status}`))));

  /** Rightmost "," or "." is the decimal separator; others are thousand separators. */
  function parseMoney(raw) {
    const s = String(raw ?? "").replace(/[\s  ]/g, "");
    const sep = Math.max(s.lastIndexOf(","), s.lastIndexOf("."));
    const n = Number.parseFloat(sep < 0 ? s : `${s.slice(0, sep).replace(/[,.]/g, "") || "0"}.${s.slice(sep + 1)}`);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  function campaigns() {
    try {
      const raw = JSON.parse(document.getElementById("free-gift-data")?.textContent || "[]");
      return raw.map((c) => {
        // Thresholds in cents per currency. The money field is in cents, in the shop's main currency.
        const thresholds = {};
        if (c.currency && Number(c.cents) > 0) thresholds[c.currency] = Number(c.cents);
        for (const line of String(c.extra || "").split(/\r?\n/)) {
          const m = line.trim().match(/^([A-Za-z]{3})\s*[=:]\s*(.+)$/);
          if (m && parseMoney(m[2]) > 0) thresholds[m[1].toUpperCase()] = Math.round(parseMoney(m[2]) * 100);
        }
        return {
          handle: c.handle,
          variantId: Number(String(c.variantId).split("/").pop()),
          available: c.available !== false,
          thresholds,
          products: (Array.isArray(c.products) ? c.products : []).map((id) => Number(id)).filter((id) => id > 0),
          startsAt: c.startsAt ? Number(c.startsAt) * 1000 : -Infinity,
          endsAt: c.endsAt ? Number(c.endsAt) * 1000 : Infinity,
        };
      });
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  const giftHandle = (item) => item.properties?.[PROP] || "";

  function hasRequiredProduct(items, productIds) {
    if (!productIds?.length) return true;
    const required = new Set(productIds);
    return items.some((item) => !giftHandle(item) && required.has(item.product_id));
  }

  function campaignMeets(c, cart, now, total) {
    const threshold = c.thresholds[cart.currency] || 0;
    const inWindow = c.startsAt <= now && now < c.endsAt && threshold > 0 && total >= threshold;
    return inWindow && hasRequiredProduct(cart.items, c.products);
  }

  let running = false;
  let again = false;

  /** One pass: returns true when it changed the cart. */
  async function sync(cart) {
    const list = campaigns();
    console.debug("[free-gift]", { currency: cart.currency, campaigns: list });
    const now = Date.now();
    // Cart value before discounts and shipping, gifts excluded.
    const total = cart.items.filter((i) => !giftHandle(i)).reduce((sum, i) => sum + i.original_line_price, 0);
    console.debug("[free-gift] total (cents)", total);
    const stored = (cart.attributes?.[ATTR] || "").split(",").filter(Boolean);
    const known = new Set(list.map((c) => c.handle));
    let declined = stored.filter((h) => known.has(h));
    let changed = false;

    for (const c of list) {
      if (!campaignMeets(c, cart, now, total)) declined = declined.filter((h) => h !== c.handle);
    }

    for (const c of list) {
      const meets = campaignMeets(c, cart, now, total);
      const lines = cart.items.map((item, i) => ({ item, line: i + 1 })).filter((l) => giftHandle(l.item) === c.handle);
      const [first, ...extra] = lines.filter((l) => l.item.variant_id === c.variantId);
      const wrong = lines.filter((l) => l.item.variant_id !== c.variantId);

      if (!meets || wrong.length || extra.length) {
        // Not (or no longer) entitled to this line: remove from the bottom so line numbers stay valid.
        const doomed = !meets ? lines : [...wrong, ...extra];
        for (const l of doomed.sort((a, b) => b.line - a.line)) cart = await post("change", { line: l.line, quantity: 0 });
        if (doomed.length) changed = true;
        // Line numbers have shifted: the change event below runs another pass for the rest.
        continue;
      }
      if (first && first.item.quantity !== 1 && meets) {
        cart = await post("change", { line: first.line, quantity: 1 });
        changed = true;
      } else if (!first && meets && !declined.includes(c.handle) && c.available) {
        cart = await post("add", { items: [{ id: c.variantId, quantity: 1, properties: { [PROP]: c.handle } }] }).then(() =>
          fetch(`${root()}.js`).then((r) => r.json()),
        );
        changed = true;
      }
    }

    // Gift line whose campaign was deleted: nothing else would remove it.
    const orphans = cart.items
      .map((item, i) => ({ item, line: i + 1 }))
      .filter((l) => giftHandle(l.item) && !known.has(giftHandle(l.item)));
    if (orphans.length && !changed) {
      for (const l of orphans.sort((a, b) => b.line - a.line)) await post("change", { line: l.line, quantity: 0 });
      return true;
    }

    if (declined.join(",") !== stored.join(",")) {
      await post("update", { attributes: { [ATTR]: declined.join(",") } });
      changed = true;
    }
    return changed;
  }

  async function run(cart) {
    if (running) {
      again = true;
      return;
    }
    running = true;
    try {
      cart = cart || (await fetch(`${root()}.js`).then((r) => r.json()));
      if (await sync(cart)) {
        const fresh = await fetch(`${root()}.js`).then((r) => r.json());
        document.dispatchEvent(new CustomEvent("theme:cart:change", { detail: { cart: fresh }, bubbles: true }));
        document.dispatchEvent(new CustomEvent("theme:cart:reload", { bubbles: true }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      running = false;
      if (again) {
        again = false;
        run();
      }
    }
  }

  document.addEventListener("theme:cart:change", (event) => {
    if (!running) run(event.detail?.cart);
    else again = true;
  });
  // Cart page / drawer opened without any cart change yet.
  if (campaigns().length || document.querySelector("[data-free-gift]")) run();

  // Customer removes a gift: remember it so the gift isn't added straight back.
  document.addEventListener(
    "click",
    async (event) => {
      const link = event.target.closest("[data-free-gift]");
      if (!link || link.dataset.freeGiftDone) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      try {
        const cart = await fetch(`${root()}.js`).then((r) => r.json());
        const handles = (cart.attributes?.[ATTR] || "").split(",").filter(Boolean);
        if (!handles.includes(link.dataset.freeGift)) handles.push(link.dataset.freeGift);
        await post("update", { attributes: { [ATTR]: handles.join(",") } });
      } catch (error) {
        console.error(error);
      }
      // Replay the click so the theme's own remove handler runs.
      link.dataset.freeGiftDone = "1";
      link.click();
    },
    true,
  );
})();
